import {
  ChangeDetectorRef,
  Component,
  AfterViewInit,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MapService } from 'src/app/services/map.service';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { combineLatest, filter, take, tap } from 'rxjs';
import { MapDataService } from 'src/app/services/map-data.service';
import { MapDetailsResponse } from './models/map-details.model';
import { imageurl, serviceurl } from 'src/app/constant/constant';
import { TerrainInfo } from 'src/app/services/terrain-layer';
import { SubscriptionStatus } from '../../subscription/models/subscription-usage.model';
import { WikiService } from 'src/app/services/wiki.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/services/toast.service';
import { MapDeleteComponent } from '../map-delete/map-delete.component';
import { Router } from '@angular/router';
import { MapInviteModalComponent } from '../../map-invite-modal/map-invite-modal.component';
import { MapImageChangeComponent } from '../map-image-change/map-image-change.component';
import { DataService } from 'src/app/services/data.service';

@Component({
    selector: 'app-map-modify',
    templateUrl: './map-modify.component.html',
    styleUrls: ['./map-modify.component.scss'],
    standalone: false
})
export class MapModifyComponent implements AfterViewInit, OnDestroy, OnInit {

  map_id: string | number = '';
  user_id: number;
  mapDetails: MapDetailsResponse | null = null;
  imageUrl: string = '';
  planUsage: SubscriptionStatus | null = null;
  todayDate: string = '';
  activePath: string = '';
  loading: boolean = false;
  terrain: TerrainInfo | null = null;
  savingSkin = false;
  readonly skinLabels: Record<string, { name: string; blurb: string }> = {
    relief: { name: 'Relief', blurb: 'Satellite-style shaded terrain' },
    atlas: { name: 'Atlas', blurb: 'Painted, parchment-toned map' },
  };

  constructor(
    private mapService: MapService,
    private mapDataService: MapDataService,
    private activatedRoute: ActivatedRoute,
    private currentUserService: CurrentUserService,
    private wikiService: WikiService,
    private modalService: NgbModal,
    private toasterService: ToastService,
    private router: Router,
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
  ) {
    this.imageUrl = imageurl;
  }
  ngOnInit(): void {
    this.activePath = localStorage.getItem('activePath');
  }

  downloadGmGuide(mapId: any): void {
    if (!mapId) return;
    this.dataService.downloadGmGuide(String(mapId)).subscribe({
      next: (res: any) => {
        const blob = res.body;
        if (blob) {
          let filename = `GM_Guide_${mapId}.pdf`;
          const contentDisposition = res.headers.get('content-disposition');
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        }
      
          this.cdr.detectChanges();},
      error: (err: any) => {
        console.warn('GM guide download error:', err);
        this.toasterService.showNote("The GM Guide isn't ready yet for this map.", 'warning');
      
          this.cdr.detectChanges();}
    });
  }

  downloadPlayerGuide(mapId: any): void {
    if (!mapId) return;
    this.dataService.downloadFieldGuide(String(mapId)).subscribe({
      next: (res: any) => {
        const blob = res.body;
        if (blob) {
          let filename = `Player_Guide_${mapId}.pdf`;
          const contentDisposition = res.headers.get('content-disposition');
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(blobUrl);
        }
      
          this.cdr.detectChanges();},
      error: (err: any) => {
        console.warn('Player guide download error:', err);
        this.toasterService.showNote("The Player Guide isn't ready yet for this map.", 'warning');
      
          this.cdr.detectChanges();}
    });
  }

  private extractParams(
    user: any,
    params: any,
    planUsage: SubscriptionStatus,
  ): void {
    this.loading = true;
    if (!user || !params) {
      console.error('User or params not found');
      return;
    }
    this.user_id = Number(user.id);
    this.map_id = params['map_id'];
    this.planUsage = planUsage;
    this.todayDate = new Date().toISOString();
    this.getCurrentMapDetails();
    this.loadTerrain();
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.currentUserService.currentUser.pipe(filter(Boolean), take(1)),
      this.activatedRoute.params.pipe(take(1)),
      this.currentUserService.planUsageData.pipe(take(1)),
    ])
      .pipe(
        tap(([user, params, planUsageData]: [any, any, SubscriptionStatus]) =>
          this.extractParams(user, params, planUsageData),
        ),
      )
      .subscribe(() => {
        this.cdr.detectChanges();
      });
  }

  /** Baked terrain status and skin for the "Map look" section. */
  private loadTerrain(): void {
    if (!this.map_id) return;
    this.dataService.getTerrainInfo(String(this.map_id)).subscribe({
      next: (info) => {
        this.terrain = info;
        this.cdr.detectChanges();
      },
      error: () => {
        this.terrain = null;
        this.cdr.detectChanges();
      },
    });
  }

  /** Two world-view tiles of the map in a skin, side by side, as a preview. */
  skinPreview(skin: string): string[] {
    const url = this.terrain?.tile_url;
    if (!url) return [];
    // the zoom at which two tiles span the map: 1 on the legacy degree grid; on the zoom
    // ladder (tiles start at the map's corner) worked out from the map's width
    const grid = this.terrain?.grid;
    const ext = this.terrain?.extent;
    const z = grid && ext ? Math.max(0, Math.floor(Math.log2((2 * 256 * grid.res0) / (ext[2] - ext[0])))) : 1;
    return [0, 1].map((x) => serviceurl + url.replace('{skin}', skin).replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', '0'));
  }

  isMapOwner(): boolean {
    return !!this.mapDetails && this.user_id == this.mapDetails?.creator?.user_id;
  }

  chooseSkin(skin: string): void {
    if (!this.terrain || this.terrain.skin === skin || this.savingSkin || !this.isMapOwner()) return;
    this.savingSkin = true;
    this.dataService.setTerrainSkin(String(this.map_id), skin).subscribe({
      next: (info) => {
        this.terrain = info;
        this.savingSkin = false;
        this.toasterService.showNote('Map look changed to ' + (this.skinLabels[skin]?.name || skin) + '.', 'success');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.savingSkin = false;
        this.toasterService.showNote(err?.error?.detail || 'Could not change the map look.', 'warning');
        this.cdr.detectChanges();
      },
    });
  }

  private getCurrentMapDetails(): void {
    this.mapDataService
      .getMapDetailsByMapId(this.map_id, this.user_id)
      .subscribe({
        next: (response: { res: MapDetailsResponse }) => {
          this.mapDetails = response.res;
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Error fetching map details:', error);
        
          this.cdr.detectChanges();},
        complete: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  viewWiki(map: MapDetailsResponse) {

    this.wikiService.openMapWiki(map.map_info.map_id, map.map_info.map_name);

    // Logic to view the wiki
  }

    isGameCreateDisabled() {
    const feature = this.planUsage?.subscription_features;
    const usage = this.planUsage?.usage;

    // If unlimited, allow

    if (feature?.max_games_created === -1) return false;

    return usage?.games_created >= feature?.max_games_created;
  }


  ngOnDestroy(): void {}

   deleteMap(map: MapDetailsResponse) {
      const deletemodalRef = this.modalService.open(MapDeleteComponent, {
        size: 'md',
        backdrop: 'static',
        centered: true,
      });
      deletemodalRef.componentInstance.mapId = map.map_info.map_id;
      deletemodalRef.componentInstance.userId = this.user_id;
      deletemodalRef.result.then((res) => {
        if (res === 'deleted') {
          this.toasterService.showSuccess('Map deleted.', 'success');
          this.router.navigate(['/maplist']);
        }
      
          this.cdr.detectChanges();});
    }

    playMap(map_id: string) {
    this.router.navigate(['/map/' + map_id]);
    // Logic to view the map
  }

   invite(map_id: string) {
      const inviteModalRef = this.modalService.open(MapInviteModalComponent, {
        size: 'md',
        backdrop: 'static',
        centered: true,
      });
      inviteModalRef.componentInstance.map_id = map_id;
      inviteModalRef.result.then((res) => {

        if (res === 'invited') {
          this.getCurrentMapDetails();
        }
      
          this.cdr.detectChanges();});
    }

   newGame(map: MapDetailsResponse) {
    this.router.navigate([
      '/creategame-mapview',
      map.map_info.map_id,
      map.map_info.map_name,
      'maplist',
    ]);
    // Logic to start a new game
  }

  updateMapImage(map: MapDetailsResponse) {
    const updateModalRef = this.modalService.open(MapImageChangeComponent, {
      size: 'md',
      backdrop: 'static',
      centered: true,
    });
    updateModalRef.componentInstance.mapId = map.map_info.map_id;
    updateModalRef.componentInstance.userId = this.user_id;
    updateModalRef.result.then((res) => {
      if (res === 'updated') {
        this.toasterService.showSuccess('Map picture updated.', 'success');
        this.getCurrentMapDetails();
        this.todayDate = new Date().toISOString();
      }
    
          this.cdr.detectChanges();});
  }

  goBack() {
    this.router.navigate(['/maplist']);
  }
  

}
